import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Box, Fab, Tooltip, Badge, useTheme, useMediaQuery } from "@mui/material";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { useEffectivePermissions, scopeSetFromPermissions } from "../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../context/AuthContext";
import { useTeamSpaces } from "../../hooks/useTeamSpaces";
import { ensureNamespaceLoaded } from "../../i18n/lazyNamespaces";
import TeamChatSpacePicker from "./TeamChatSpacePicker";
import TeamChatFloatingWindow from "./TeamChatFloatingWindow";
import { useRealtimeTeamMessages } from "../../hooks/useRealtimeTeamMessages";
import { TEAM_MOBILE_MEDIA_QUERY } from "../../pages/Team/utils/teamUiUtils";
import { isServiceChatPath } from "../../utils/teamNavigation";
import { useAppUnreadBadge } from "../../hooks/useAppUnreadBadge";
import { openTeamPopupWindow } from "../../utils/openTeamPopupWindow";

const STORAGE_KEY = "xproTeamFabPosition";
const FAB_SIZE = 56;
const MARGIN = 16;
const TOPBAR_OFFSET = 64;
const DRAG_THRESHOLD = 5;
const DOUBLE_CLICK_MS = 450;
const BASE_Z = 1200;

function loadStoredPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (Number.isFinite(p?.x) && Number.isFinite(p?.y)) {
      return { x: p.x, y: p.y };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function defaultPosition(isMobile = false) {
  if (typeof window === "undefined") return { x: MARGIN, y: 24 };
  if (isMobile) {
    return {
      x: Math.max(MARGIN, window.innerWidth - FAB_SIZE - MARGIN),
      y: Math.max(TOPBAR_OFFSET + MARGIN, window.innerHeight - FAB_SIZE - MARGIN - 12),
    };
  }
  return {
    x: MARGIN,
    y: Math.max(MARGIN, window.innerHeight - FAB_SIZE - MARGIN - TOPBAR_OFFSET),
  };
}

function clampPosition(pos) {
  if (typeof window === "undefined") return pos;
  const maxX = Math.max(MARGIN, window.innerWidth - FAB_SIZE - MARGIN);
  const maxY = Math.max(MARGIN, window.innerHeight - FAB_SIZE - MARGIN);
  return {
    x: Math.min(maxX, Math.max(MARGIN, pos.x)),
    y: Math.min(maxY, Math.max(MARGIN, pos.y)),
  };
}

function computeWindowPosition(index, fabPosition) {
  const width = 340;
  const height = 420;
  const margin = 16;
  const stack = index * 24;
  const w = window.innerWidth || 0;
  const h = window.innerHeight || 0;

  let x = fabPosition.x - width - margin + stack;
  let y = fabPosition.y - height + stack;

  if (x < margin) {
    x = Math.max(margin, fabPosition.x + FAB_SIZE + margin + stack);
  }
  x = Math.min(x, w - width - margin);
  y = Math.max(TOPBAR_OFFSET, Math.min(y, h - height - margin));

  return { x, y };
}

const TeamChatWidget = () => {
  const { t } = useTranslation("app");
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(TEAM_MOBILE_MEDIA_QUERY);
  const onTeamPage = String(location.pathname || "").startsWith("/equipe");
  /** No mobile o FAB cobre o botão de envio do composer do atendimento. */
  const onServiceChat = isServiceChatPath(location.pathname);
  const fabRef = useRef(null);
  const { userData } = useAuthContext();
  const { data: permissionsData } = useEffectivePermissions();
  const scopes = scopeSetFromPermissions(permissionsData, userData);
  const canView = scopes.has("team_chat.view");

  const currentUserId = useMemo(() => userData?.id ?? null, [userData?.id]);

  const {
    spaces,
    refetch,
    markRead,
    createChannel,
    createDm,
    isCreating,
  } = useTeamSpaces();
  const unreadTotal = useMemo(
    () => (spaces || []).reduce((sum, s) => sum + (Number(s.unreadCount) || 0), 0),
    [spaces]
  );

  useAppUnreadBadge(canView ? unreadTotal : 0);

  const fabTooltip = useMemo(() => {
    if (unreadTotal <= 0) {
      return t("nav.team", { defaultValue: "Equipe" });
    }
    const shown = unreadTotal > 99 ? "99+" : unreadTotal;
    return t("nav.teamUnread", {
      count: shown,
      defaultValue: `Equipe (${shown} não lidas)`,
    });
  }, [t, unreadTotal]);

  useRealtimeTeamMessages({ refetchSpaces: refetch });

  const [position, setPosition] = useState(() =>
    clampPosition(loadStoredPosition() || defaultPosition(false))
  );
  const [dragging, setDragging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openWindows, setOpenWindows] = useState([]);
  const zCounterRef = useRef(BASE_Z);

  const dragState = useRef({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
  });
  const singleClickTimerRef = useRef(null);
  const lastClickAtRef = useRef(0);

  const clearSingleClickTimer = useCallback(() => {
    if (singleClickTimerRef.current) {
      clearTimeout(singleClickTimerRef.current);
      singleClickTimerRef.current = null;
    }
  }, []);

  const openFullTeamPage = useCallback(() => {
    clearSingleClickTimer();
    lastClickAtRef.current = 0;
    setPickerOpen(false);
    openTeamPopupWindow();
  }, [clearSingleClickTimer]);

  useEffect(() => () => clearSingleClickTimer(), [clearSingleClickTimer]);

  useEffect(() => {
    if (canView) void ensureNamespaceLoaded("team");
  }, [canView]);

  const persistPosition = useCallback((pos) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    const onResize = () => {
      setPosition((prev) => {
        if (isMobile && !loadStoredPosition()) {
          return clampPosition(defaultPosition(true));
        }
        return clampPosition(prev);
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (loadStoredPosition()) return;
    setPosition(clampPosition(defaultPosition(true)));
  }, [isMobile]);

  const bringToFront = useCallback((spaceId) => {
    setOpenWindows((prev) => {
      const target = prev.find((w) => w.spaceId === spaceId);
      if (!target) return prev;
      const z = ++zCounterRef.current;
      return [...prev.filter((w) => w.spaceId !== spaceId), { ...target, zIndex: z }];
    });
  }, []);

  const handleCreateChannel = useCallback(
    async (payload) => {
      const res = await createChannel(payload);
      const updated = await refetch();
      const list = updated?.data || spaces;
      const created = list.find((s) => Number(s.id) === Number(res?.id));
      return { ...res, space: created || (res?.id ? { id: res.id, type: "channel" } : null) };
    },
    [createChannel, refetch, spaces]
  );

  const handleCreateDm = useCallback(
    async (payload) => {
      const res = await createDm(payload);
      const updated = await refetch();
      const list = updated?.data || spaces;
      const created = list.find((s) => Number(s.id) === Number(res?.id));
      return { ...res, space: created || (res?.id ? { id: res.id, type: "dm" } : null) };
    },
    [createDm, refetch, spaces]
  );

  const openWindow = useCallback(
    (space) => {
      const spaceId = Number(space.id);
      if (isMobile) {
        setPickerOpen(false);
        markRead(spaceId)
          .catch(() => {})
          .finally(() => refetch());
        openTeamPopupWindow(spaceId || undefined);
        return;
      }
      setPickerOpen(false);
      setOpenWindows((prev) => {
        const existing = prev.find((w) => w.spaceId === spaceId);
        if (existing) {
          const z = ++zCounterRef.current;
          return [
            ...prev.filter((w) => w.spaceId !== spaceId),
            { ...existing, zIndex: z, expanded: true },
          ];
        }
        const z = ++zCounterRef.current;
        return [
          ...prev,
          {
            spaceId,
            position: computeWindowPosition(prev.length, position),
            expanded: true,
            zIndex: z,
          },
        ];
      });
      markRead(spaceId)
        .catch(() => {})
        .finally(() => refetch());
    },
    [isMobile, markRead, position, refetch]
  );

  const onPointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
    dragState.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      originX: position.x,
      originY: position.y,
    };
  };

  const onPointerMove = (e) => {
    if (!dragState.current.active) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (
      !dragState.current.moved &&
      (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)
    ) {
      dragState.current.moved = true;
      clearSingleClickTimer();
      lastClickAtRef.current = 0;
      setPickerOpen(false);
    }
    setPosition(
      clampPosition({
        x: dragState.current.originX + dx,
        y: dragState.current.originY + dy,
      })
    );
  };

  const onPointerUp = (e) => {
    if (!dragState.current.active) return;
    dragState.current.active = false;
    setDragging(false);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    setPosition((prev) => {
      persistPosition(prev);
      return prev;
    });
    if (!dragState.current.moved) {
      const now = Date.now();
      if (lastClickAtRef.current && now - lastClickAtRef.current <= DOUBLE_CLICK_MS) {
        openFullTeamPage();
        return;
      }
      lastClickAtRef.current = now;
      clearSingleClickTimer();
      singleClickTimerRef.current = setTimeout(() => {
        singleClickTimerRef.current = null;
        lastClickAtRef.current = 0;
        setPickerOpen((open) => !open);
      }, DOUBLE_CLICK_MS);
    }
  };

  const onDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openFullTeamPage();
  };

  if (!canView) return null;
  if (isMobile && (onTeamPage || onServiceChat)) return null;

  return (
    <>
      <Box
        ref={fabRef}
        sx={{
          position: "fixed",
          left: position.x,
          top: position.y,
          zIndex: theme.zIndex.speedDial,
          touchAction: isMobile ? "manipulation" : "none",
          cursor: isMobile ? "pointer" : dragging ? "grabbing" : "grab",
          pb: isMobile ? "env(safe-area-inset-bottom, 0px)" : 0,
        }}
      >
        <Tooltip title={fabTooltip} placement="right">
          <Badge
            color="error"
            badgeContent={unreadTotal > 99 ? "99+" : unreadTotal}
            invisible={unreadTotal <= 0}
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              overflow: "visible",
              "& .MuiBadge-badge": {
                top: 6,
                right: 6,
                zIndex: 2,
                fontWeight: 700,
                fontSize: "0.7rem",
                minWidth: 20,
                height: 20,
                boxShadow: (th) => `0 0 0 2px ${th.palette.primary.main}`,
              },
            }}
          >
            <Fab
              color="primary"
              aria-label={t("nav.team", { defaultValue: "Equipe" })}
              onClick={isMobile ? () => openTeamPopupWindow() : undefined}
              onDoubleClick={isMobile ? undefined : onDoubleClick}
              onPointerDown={isMobile ? undefined : onPointerDown}
              onPointerMove={isMobile ? undefined : onPointerMove}
              onPointerUp={isMobile ? undefined : onPointerUp}
              onPointerCancel={isMobile ? undefined : onPointerUp}
              sx={{
                cursor: "inherit",
                position: "relative",
                zIndex: 0,
                boxShadow: theme.shadows[8],
                "&:hover": { boxShadow: theme.shadows[12] },
              }}
            >
              <ForumOutlinedIcon />
            </Fab>
          </Badge>
        </Tooltip>
      </Box>

      <TeamChatSpacePicker
        anchorEl={fabRef.current}
        open={pickerOpen && !isMobile}
        onClose={() => setPickerOpen(false)}
        spaces={spaces}
        onSelectSpace={openWindow}
        onCreateChannel={handleCreateChannel}
        onCreateDm={handleCreateDm}
        isCreating={isCreating}
        canCreateChannel={canView}
        currentUserId={currentUserId}
        onRefresh={refetch}
      />

      {!isMobile
        ? openWindows.map((win) => {
        const space = spaces.find((s) => Number(s.id) === Number(win.spaceId));
        if (!space) return null;
        return (
          <TeamChatFloatingWindow
            key={win.spaceId}
            space={space}
            spaces={spaces}
            position={win.position}
            expanded={win.expanded}
            zIndex={win.zIndex}
            markRead={markRead}
            refetchSpaces={refetch}
            onFocus={() => bringToFront(win.spaceId)}
            onPositionChange={(pos) => {
              setOpenWindows((prev) =>
                prev.map((w) => (w.spaceId === win.spaceId ? { ...w, position: pos } : w))
              );
            }}
            onExpandedChange={(expanded) => {
              setOpenWindows((prev) =>
                prev.map((w) => (w.spaceId === win.spaceId ? { ...w, expanded } : w))
              );
            }}
            onClose={() => {
              setOpenWindows((prev) => prev.filter((w) => w.spaceId !== win.spaceId));
            }}
          />
        );
      })
        : null}
    </>
  );
};

export default TeamChatWidget;
